import { createSelector } from '@ngrx/store';
import { DeliveryWindowType } from 'graphql-api/gql-api-interfaces';

import {
  ProductExtendedVariant,
  ProductType,
  ProductVariant,
} from '../../models';
import { entitiesToArray } from '../../shared/entities-to-array';
import { selectConfigStripState } from '../config-strip.feature.selector';

export const selectDataConfigStripState = createSelector(
  selectConfigStripState,
  (state) => state.data,
);

export const selectProduct = createSelector(
  selectDataConfigStripState,
  (data): ProductType | null => data.product,
);
export const selectProductId = createSelector(
  selectProduct,
  (product) => product?.productId ?? null,
);
export const selectProductBrandId = createSelector(
  selectProduct,
  (product) => product?.brandId ?? null,
);
export const selectDeliveryWindowType = createSelector(
  selectProduct,
  (product) => product?.deliveryWindowType ?? DeliveryWindowType.Month,
);
export const selectProductAttributes = createSelector(
  selectProduct,
  (product) => product?.attributes,
);
export const selectProductDescriptionAttributes = createSelector(
  selectProduct,
  (product) => product?.descriptionAttributes,
);

export const selectVariantEntities = createSelector(
  selectDataConfigStripState,
  (data) => data.variants,
);
export const selectVariants = createSelector(
  selectVariantEntities,
  (variants): ProductVariant[] => Object.values(variants),
);
export const selectExtendedVariants = createSelector(
  selectProduct,
  selectVariants,
  (product, variants): ProductExtendedVariant[] =>
    variants.map((variantItem) => ({
      ...variantItem,
      articleId: product?.productId ?? null,
    })),
);

export const selectPrices = createSelector(
  selectDataConfigStripState,
  (data) => data.prices ?? null,
);

export const selectDeliveryShops = createSelector(
  selectDataConfigStripState,
  (data) => data.deliveryShops ?? [],
);

export const selectDeliveryShopsCount = createSelector(
  selectDeliveryShops,
  (shops) => shops?.length ?? 0,
);

export const selectSizeEntities = createSelector(
  selectDataConfigStripState,
  (data) => data.sizes,
);
export const selectSizes = createSelector(selectSizeEntities, entitiesToArray);

export const selectPrepackEntities = createSelector(
  selectDataConfigStripState,
  (data) => data.prepacks,
);

export const selectPrepacks = createSelector(
  selectPrepackEntities,
  entitiesToArray,
);

export const selectValidators = createSelector(
  selectDataConfigStripState,
  (data) => data.validators,
);
export const selectScaleSets = createSelector(
  selectDataConfigStripState,
  (data) => data.scaleSets,
);
export const selectPrepackInventories = createSelector(
  selectPrepacks,
  (prepacks) => prepacks.flatMap((prepack) => prepack.inventoryItems ?? []),
);
export const selectSizesInventories = createSelector(selectSizes, (sizes) =>
  sizes.flatMap((size) => size.inventoryItems ?? []),
);
export const selectSizeRuns = createSelector(
  selectDataConfigStripState,
  (state) => state.sizeRuns,
);

export const selectProductIsLocked = createSelector(
  selectDataConfigStripState,
  (state) => state.product?.isLocked,
);
